import { css } from "@compiled/react";

import { media } from "@atlaskit/primitives/responsive";
import { token } from "@atlaskit/tokens";

import { wacToken } from "@/libraries/design/foundations";

export type FeaturedInfoHeaderVariant =
    | typeof PLAN
    | typeof TRACK
    | typeof LAUNCH
    | typeof REPORT;

export type FeaturedInfoHeaderProps = {
    variant: FeaturedInfoHeaderVariant;
    eyebrow: string;
    header: string;
    description: string;
};

export const PLAN = "plan";
export const TRACK = "track";
export const LAUNCH = "launch";
export const REPORT = "report";

const containerStyles = css({
    "position": "relative",
    "maxWidth": "1301px",
    "margin": "0 auto",
    "overflowX": "clip",
    "overflowClipMargin": "border-box",
    "zIndex": 0,

    "&::before": {
        content: "",
        position: "absolute",
        pointerEvents: "none",
        width: "1301px",
        height: "max(339px, calc(100% + 65px))",
        zIndex: -1,
        inset: "0 0 0 50%",
        transform: "translateX(-50%)"
    }
});

const planVariantStyles = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".h2": {
        color: wacToken("color.neutral.light0")
    },

    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".body-md": {
        color: wacToken("color.neutral.light0")
    },

    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".eyebrow-md": {
        color: wacToken("color.neutral.light0")
    },

    "&::before": {
        backgroundColor: wacToken("color.primary.blue"),
        clipPath:
            "polygon(1163.600px 14.108px, 0.000px 0.120px, 8.861px 100%, 1167.000px 100%, 1167.000px 100%, 1301.000px 100%, 1301.000px 66.000px, 1164.150px 66.000px, 1163.600px 14.108px)",

        [media.below.lg]: {
            inset: "0 0 0 auto",
            transform: "none"
        },

        [media.below.sm]: {
            inset: "0 -10% 0 auto"
        },

        [media.below.xs]: {
            inset: "0 -20% 0 auto"
        }
    }
});

const trackVariantStyles = css({
    "&::before": {
        backgroundColor: wacToken("color.extended.lime400"),
        width: "1295px",
        clipPath:
            "polygon(0.000px 0.120px, 704.881px 10.712px, 704.881px 33.269px, 1295.000px 57.207px, 1275.200px 100%, 0.001px 100%, 0.000px 0.120px)",

        [media.below.sm]: {
            height: "max(399px, calc(100% + 65px))"
        }
    }
});

const launchVariantStyles = css({
    "&::before": {
        backgroundColor: wacToken("color.extended.saffron200"),
        height: "314px",
        clipPath:
            "polygon(0.000px 0.518px, 1182.710px 20.927px, 1184.000px 100%, 0.000px 100%, 0.000px 0.518px, 1122.000px 39.518px, 1301.000px 39.518px, 1301.000px 100%, 1122.000px 100%, 1122.000px 39.518px)",

        [media.below.lg]: {
            inset: "0 0 0 auto",
            transform: "none"
        },

        [media.below.md]: {
            height: "max(339px, calc(100% + 65px))",
            inset: "0 -5% 0 auto"
        },

        [media.below.sm]: {
            inset: "0 -20% 0 auto"
        }
    }
});

const reportVariantStyles = css({
    "&::before": {
        backgroundColor: wacToken("color.extended.purple400"),
        height: "max(356px, calc(100% + 65px))",
        clipPath:
            "polygon(1245.640px 14.061px, 0.000px 0.518px, 0.000px 100%, 1301.000px 100%, 1301.000px 84.518px, 1245.920px 84.518px, 1245.640px 14.061px)",
        [media.below.lg]: {
            inset: "0 0 0 auto",
            transform: "none"
        },

        [media.below.md]: {
            inset: "0 -3% 0 auto"
        },

        [media.below.sm]: {
            inset: "0 -5% 0 auto"
        },

        [media.below.xs]: {
            inset: "0 -10% 0 auto"
        }
    }
});

const headerStyles = css({
    "display": "flex",
    "flexDirection": "column",
    "gap": token("space.100"),
    "margin": "0 auto 32px auto",
    "maxWidth": "min(1168px, calc(100% - 120px))",
    "padding": "55px 0 0 0",

    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".h2": {
        margin: "0",
        maxWidth: "768px"
    },

    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".body-md": {
        margin: "0",
        maxWidth: "768px"
    },

    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".eyebrow-md": {
        margin: "0"
    },

    [media.below.sm]: {
        maxWidth: "calc(100% - 64px)",
        padding: "55px 0 0 0"
    }
});

const eyebrowStyles = css({
    order: -1,
    display: "flex",
    flexDirection: "column",
    gap: "14px"
});

const PlanEyelash = () => (
    <svg
        width="46"
        height="26"
        viewBox="0 0 46 26"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
    >
        <path
            d="M27.8179 8.5621L28.9492 7.54226C28.9492 7.54226 29.1999 6.94045 28.9993 6.53364C28.8767 6.28288 28.6873 6.03769 28.4811 5.84822C28.0631 5.46928 27.5672 5.34664 27.3498 5.94291C27.322 5.92619 27.2774 5.92622 27.2384 5.89835C27.1604 6.65623 26.2241 6.69528 25.8842 7.25812C26.018 7.35285 25.8731 7.28036 25.8731 7.28036C25.9232 7.87663 25.3548 8.2166 25.3548 8.22217C24.6137 9.36457 23.2539 10.6407 22.5908 11.9949L22.6186 12.006L22.641 12.0283C22.8862 12.0506 22.5295 12.3849 22.4905 12.2512C21.7047 12.658 20.646 13.8896 20.4899 14.7589C20.6571 14.8703 20.5679 15.0208 20.5233 14.9539C19.9605 14.9149 15.4912 19.7742 17.8819 19.6739L18.0268 19.8411C18.3556 19.5848 20.6292 17.6177 20.6459 17.64C21.1029 17.8796 21.0193 15.7174 21.493 16.6982C23.9951 16.2301 27.0991 10.5125 27.0991 10.5125L27.1715 9.83268C27.1715 9.83268 28.0742 9.27539 27.8123 8.55652L27.8179 8.5621Z"
            fill={wacToken("color.neutral.light0")}
        />
        <path
            d="M8.85401 4.95098L8.60878 4.31569C8.60878 4.31569 9.15488 3.40732 8.59204 2.88906L9.12706 1.4625C9.12706 1.4625 9.07134 0.810485 8.70912 0.542998C8.48064 0.375818 8.202 0.242056 7.93451 0.175184C7.39396 0.0302949 6.89243 0.14737 6.97045 0.782652C6.93701 0.782652 6.89795 0.799313 6.8478 0.79374C7.12643 1.50147 6.31288 1.96401 6.27387 2.62159C6.43548 2.64388 6.55806 2.69403 6.4689 2.69403L6.26829 2.64948C6.58593 3.15659 5.88373 3.29032 6.49672 3.81972L6.2404 3.71938C6.10666 5.07911 5.4825 6.82894 5.52151 8.33913H5.55498C5.55498 8.33913 5.64414 8.71807 5.55498 8.61776C5.04229 9.34221 4.67449 10.9193 4.92526 11.7607C5.12588 11.783 2.79095 18.5148 4.86397 17.3278L5.07016 17.4114C5.24291 17.0325 5.3822 16.7149 5.87816 16.3861C5.85587 16.3192 5.90047 16.3247 5.68314 16.2802C5.65527 16.2579 5.594 16.2189 5.50484 16.2077C4.98101 16.1074 6.70848 13.0257 6.70848 13.0257C8.71464 11.4598 8.84843 4.95656 8.84843 4.95656L8.85401 4.95098Z"
            fill={wacToken("color.neutral.light0")}
        />
        <path
            d="M44.1735 17.8016C43.984 17.2722 43.6106 16.9267 43.1314 17.3503C43.1147 17.3224 43.0756 17.3001 43.0534 17.2555C42.6243 17.8852 41.7828 17.4728 41.2199 17.8128C41.2924 17.9577 41.1976 17.8295 41.1976 17.8295C40.958 18.3756 40.3004 18.409 40.3004 18.4146C39.1023 19.0722 37.3024 19.5459 36.082 20.4264L36.0987 20.4486L36.1098 20.4765C36.316 20.6103 35.8423 20.7384 35.8702 20.5991C34.9841 20.5824 33.4684 21.1675 32.9222 21.8529C33.017 22.0313 33.0672 21.5297 33.3792 22.1037C33.2009 22.1873 33.056 22.3545 32.8777 22.4214C32.7105 22.0313 32.8665 22.1204 32.8554 22.0424C32.3761 21.7415 26.1404 23.9037 28.2914 24.9458L28.3415 25.1631C28.7539 25.0907 29.6232 24.9235 29.5842 24.8399C29.3725 24.3551 31.6907 24.4331 31.6963 24.4665L32.8888 24.0374C35.3129 24.8065 40.7574 21.2511 40.7574 21.2511L41.1475 20.6883C41.1475 20.6883 42.2063 20.627 42.3177 19.8691L43.7945 19.5069C43.7945 19.5069 44.3016 19.0945 44.3128 18.6431C44.3239 18.3645 44.2737 18.0579 44.1846 17.796L44.1735 17.8016Z"
            fill={wacToken("color.neutral.light0")}
        />
    </svg>
);

const TrackEyelash = () => (
    <svg
        width="43"
        height="20"
        viewBox="0 0 43 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
    >
        <path
            d="M41.1672 6.20799C29.1522 -1.68807 19.1878 -0.0992864 12.5988 1.16576C9.10339 0.0506007 -6.62876 8.95992 3.16787 6.59171C7.3767 3.10833 21.6519 1.39363 29.518 4.72712C31.8203 2.91049 46.5871 12.3833 41.1612 6.20799H41.1672Z"
            fill={wacToken("color.primary.black")}
        />
        <path
            d="M34.6504 11.8079C29.9799 7.55705 23.1151 8.00677 20.2852 8.34252C22.5635 6.96356 16.5019 7.59904 15.5187 8.30051C16.478 8.24055 15.9745 8.85811 15.1771 8.92406C14.9252 7.26931 4.39719 11.1483 7.26304 13.1508C8.54008 12.4853 9.17553 13.0789 9.28944 11.7599C9.93096 13.3247 11.244 11.0884 11.6816 10.9145C12.6169 11.2203 12.593 11.598 13.8101 10.6927C15.4408 10.6927 17.773 11.1424 20.513 10.6328C24.3321 10.5069 26.4066 11.688 29.5662 11.9937C30.8972 12.4854 37.5462 15.627 34.6624 11.8199L34.6504 11.8079Z"
            fill={wacToken("color.primary.black")}
        />
        <path
            d="M28.9065 17.8751C27.0359 16.2983 24.6736 15.369 21.3641 15.5788C22.7551 16.736 21.4541 16.706 20.5668 16.1484C22.3295 14.8773 16.7837 15.1892 18.2106 16.2803C17.8389 16.5441 17.4252 16.3822 17.707 16.0824C15.6385 15.1412 9.31332 19.7457 14.4574 18.7505C14.2176 17.9351 15.1289 19.9256 15.8064 17.977C16.5978 18.5166 16.3459 18.8104 17.0414 18.061C17.7728 18.109 19.2118 19.0083 20.6807 18.4327C22.7731 17.9411 23.5346 19.7757 25.1054 18.7025C26.0407 19.5059 30.5013 20.3512 28.8945 17.8691L28.9065 17.8751Z"
            fill={wacToken("color.primary.black")}
        />
    </svg>
);

const LaunchEyelash = () => (
    <svg
        width="58"
        height="20"
        viewBox="0 0 58 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
    >
        <path
            d="M50.3635 18.9904C50.6395 18.4167 52.3934 17.7903 52.2324 17.1698C52.4164 17.2517 53.1467 16.561 53.0374 15.9171C55.1363 14.4477 58 10.0161 58 10.0161C58 10.0161 56.3036 6.15833 54.13 5.07533C54.0725 5.15729 48.1151 -1.82082 47.7068 1.34037C48.2186 1.5277 48.3221 3.24884 48.8742 2.55806C49.2939 1.75606 50.2887 3.78741 50.6568 3.86937C50.3865 4.7182 51.2548 5.73687 51.8758 5.83053C52.7844 7.10671 54.13 8.3418 54.13 8.3418C54.13 8.3418 47.3963 7.55744 45.1594 8.86289C44.1933 8.29505 25.6081 7.85015 32.6236 9.02096C31.1572 9.98688 25.2516 8.65797 29.1676 8.4004C18.5984 8.14282 13.0263 7.32917 0.979278 10.8123C-2.42494 12.3402 4.08448 12.1236 4.77452 11.5967C4.87803 11.1342 4.90103 10.8474 5.37257 10.9177C5.52208 12.6446 9.75435 10.2855 10.2604 10.3674C17.0631 11.4797 31.4678 10.461 40.2601 11.9128C42.3532 11.7957 51.0305 10.9411 52.5141 10.9411C53.9977 10.9411 53.0202 11.6084 52.0139 12.3695C51.9794 12.3695 51.9736 12.4456 51.8758 12.3812C51.7723 14.3189 50.444 15.8408 48.9087 17.1287C48.0806 17.7844 49.2537 20.694 50.3635 18.9904Z"
            fill={wacToken("color.primary.black")}
        />
    </svg>
);

const ReportEyelash = () => (
    <svg
        width="51"
        height="50"
        viewBox="0 0 51 50"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
    >
        <path
            d="M30.3513 23.6639C31.5684 22.8331 35.6575 18.0452 31.7445 19.1689C30.6929 20.0051 25.8724 26.4493 23.2247 29.7726C22.6428 28.4092 20.9719 26.9179 19.7495 26.1617C19.0074 25.4747 18.7085 26.6091 18.7085 26.6091C18.9167 26.9127 19.1356 27.392 19.6427 27.7754C20.2085 28.2015 20.2833 29.0855 21.2121 29.7619C24.4792 32.1319 23.0058 35.6202 26.1661 29.5754C27.2551 26.9232 29.9882 26.0605 30.3459 23.6639H30.3513Z"
            fill={wacToken("color.primary.black")}
        />
        <path
            d="M50.9995 23.467C51.0262 16.5808 49.8999 5.78014 42.3943 3.2291C47.5991 10.1845 50.754 21.3899 47.8553 30.0123C45.1702 43.6729 24.4417 51.3366 11.9501 43.4225C13.2313 44.6368 16.162 46.0163 17.961 46.5488C13.1886 47.582 2.45871 35.5564 10.0551 42.1018C-11.5009 14.6262 22.6267 -11.651 42.2715 12.0806C36.6983 1.31722 22.2263 -2.3629 13.3114 2.87232C-29.6403 36.808 44.5296 76.9961 50.9942 23.467H50.9995Z"
            fill={wacToken("color.primary.black")}
        />
    </svg>
);

export const FeaturedInfoHeader = ({
    variant,
    eyebrow,
    header,
    description
}: FeaturedInfoHeaderProps) => {
    return (
        <div
            css={[
                containerStyles,
                variant === PLAN && planVariantStyles,
                variant === TRACK && trackVariantStyles,
                variant === LAUNCH && launchVariantStyles,
                variant === REPORT && reportVariantStyles
            ]}
        >
            <section css={headerStyles}>
                <h2 className="h2">{header}</h2>
                <div css={eyebrowStyles}>
                    {variant === PLAN && <PlanEyelash />}
                    {variant === TRACK && <TrackEyelash />}
                    {variant === LAUNCH && <LaunchEyelash />}
                    {variant === REPORT && <ReportEyelash />}
                    <h3 className="eyebrow-md">{eyebrow}</h3>
                </div>
                <p className="body-md">{description}</p>
            </section>
        </div>
    );
};
